import { useRef, useState } from "react";

import Box from "@/components/Box";
import { CMP_PERMISSION_ADMIN } from "@/components/organisations/constants";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { DELETE_INVITE_MUTATION, RESEND_INVITE_MUTATION } from "@/graphql/organisations/mutations";
import Center from "@/tpo/Center";
import ChevronComponent from "@/tpo/Chevron";
import Group from "@/tpo/Group";
import { Error } from "@/tpo/InputWrapper";
import Modal from "@/tpo/Modal";
import Spacer from "@/tpo/Spacer";
import Stack from "@/tpo/Stack";
import ButtonV2, { IndicativeButton } from "@/v2/Buttons";
import { useMutation } from "@apollo/client";

export function PractitionerInviteControls({
  status,
  inviteId,
  organisationPractitionerId,
  onSuccess = () => {}
}) {
  const resendButtonRef = useRef();
  const deleteButtonRef = useRef();
  const [showDeleteInviteConfirmation, setShowDeleteInviteConfirmation] = useState(false);

  const [resendInviteMutation] = useMutation(RESEND_INVITE_MUTATION, {
    onCompleted: () => {
      resendButtonRef.current.setSuccessful(true);
      resendButtonRef.current.setPending(false);
      onSuccess();
    },
    onError: () => {
      resendButtonRef.current.setSuccessful(false);
      resendButtonRef.current.setPending(false);
    }
  });

  const getNonFieldError = data =>
    data?.deleteOrganisationPractitionerMutation?.errors?.find(error => error.field === "_All__")
      ?.messages?.[0];

  const [deleteInviteMutation, { data }] = useMutation(DELETE_INVITE_MUTATION, {
    onCompleted: data => {
      if (getNonFieldError(data)) {
        deleteButtonRef.current.setSuccessful(false);
        deleteButtonRef.current.setPending(false);
      } else {
        onSuccess();
        deleteButtonRef.current.setSuccessful(true);
        deleteButtonRef.current.setPending(false);
      }
    },
    onError: () => {
      deleteButtonRef.current.setSuccessful(false);
      deleteButtonRef.current.setPending(false);
    }
  });

  const { organisation, permissions } = useOrganisationContext();
  const canDeletePractitioner = permissions?.includes(CMP_PERMISSION_ADMIN);

  const nonFieldError = getNonFieldError(data);

  return (
    <Stack ml={[0, "auto"]} gap={10} justifyContent="flex-end">
      {status === "Invited" && inviteId && (
        <IndicativeButton
          ref={resendButtonRef}
          defaultColor="dark"
          pendingColor="darkgrey"
          successColor="#2ecc71"
          failureColor="error"
          rightIcon={<ChevronComponent />}
          type="button"
          onClick={() => {
            resendInviteMutation({
              variables: {
                organisationId: parseInt(organisation?.id),
                inviteId
              }
            });
          }}
          size="sm"
        >
          resend
        </IndicativeButton>
      )}
      {status === "Connected" && canDeletePractitioner && (
        <>
          <ButtonV2
            ref={deleteButtonRef}
            color="red"
            disabled={!organisationPractitionerId}
            onClick={() => {
              setShowDeleteInviteConfirmation(true);
            }}
            type="button"
            size="sm"
          >
            delete
          </ButtonV2>
          <Modal
            maxWidth={600}
            headerProps={{
              p: [20, 20, 40]
            }}
            bg="white"
            show={showDeleteInviteConfirmation}
            close={() => setShowDeleteInviteConfirmation(null)}
            mode={["fullScreen", "fullScreen", "centered"]}
          >
            <Box maxWidth={520} mx="auto">
              <Box pt={6} pb={6} px={20}>
                <Box fontFamily="gilroyBold" fontSize={28}>
                  Are you sure?
                </Box>
                <Spacer py={15} />
                <Box fontFamily="gilroyMedium" fontSize={16}>
                  You are about to remove a practitioner from your organisation.
                </Box>
                <Spacer py={15} />
                <Group gap={20} flexDirection="row" flexWrap="wrap">
                  <IndicativeButton
                    ref={deleteButtonRef}
                    defaultColor="red"
                    pendingColor="darkgrey"
                    successColor="#2ecc71"
                    failureColor="error"
                    onClick={() => {
                      deleteInviteMutation({
                        variables: {
                          input: {
                            organisation: parseInt(organisation?.id),
                            organisationPractitionerId
                          }
                        }
                      });
                    }}
                  >
                    delete
                  </IndicativeButton>
                  <ButtonV2
                    color="dark"
                    onClick={() => {
                      setShowDeleteInviteConfirmation(null);
                    }}
                  >
                    cancel
                  </ButtonV2>
                </Group>
                {nonFieldError && (
                  <Center mt={[20, 20, 40]}>
                    <Error error={nonFieldError} />
                  </Center>
                )}
              </Box>
            </Box>
          </Modal>
        </>
      )}
    </Stack>
  );
}
